import { ConfigProvider, Form, Input, message, Modal, Select, Button } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { UnitTypeModel } from "@/core/models/unit-type/unit-type.model";
import { UnitTypeRepo } from "@/core/repos/tables/unit-type.repo";
import { FeeLadderModel } from "@/core/models/fee-ladder.model";
import { FeeLadderRepo } from "@/core/repos/tables/fee-ladder.repo";
import { Province } from "@/core/models/address/province.model";
import { AddressRepo } from "@/core/repos/address.repo";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { SyncOutlined } from '@ant-design/icons';
import { useDrafts } from '@/core/stores/drafts.store';
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';

const unitTypeRepo = container.get(UnitTypeRepo);
const feeLadderRepo = container.get(FeeLadderRepo);
const addressRepo = container.get(AddressRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: UnitTypeModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<UnitTypeModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [feeLadders, setFeeLadders] = useState<FeeLadderModel[]>([]);
    const [provinces, setProvinces] = useState<Province[]>([]);
    // Draft logic for UnitTypeModel
    const draft = useDrafts((state) => state.getDraft('UnitTypeModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft('UnitTypeModel', form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('UnitTypeModel');
        form.setFieldsValue(data);
    };
    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        form.setFieldsValue(data);
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
        } else {
            if (draft) form.setFieldsValue(draft);
            setIsEdit(false);
        }
    }
    useEffect(() => {
        handleDataChange();
    }, [data])

    const populateSelections = async () => {
        const _feeLadders = await feeLadderRepo.getAll();
        setFeeLadders(_feeLadders);

        const _provinces = (await addressRepo.getAllProvinces()).map(data => data[1]);
        setProvinces(_provinces);
    }

    // Initialize select options data
    const init = async () => {
        await populateSelections();
    }
    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);


    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await unitTypeRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật bản ghi"
                    })
                } else {
                    messageApi.error({
                        content: "Cập nhật bản ghi thất bại"
                    })
                }
            } else {
                const result = await unitTypeRepo.create({...data, ...formData});
                setIsModalOpen(false);
                clearDraft();
                if (result) {
                    messageApi.success({
                        content: "Thêm bản ghi mới thành công"
                    })
                } else {
                    messageApi.error({
                        content: "Thêm bản ghi mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal open={isModalOpen} onCancel={handleCancel} width={500} centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
            ]}>
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT LOẠI HÌNH ĐƠN VỊ</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-3">
                    <Form<UnitTypeModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        <Form.Item<UnitTypeModel>
                            label="Tên"
                            name="name"
                            rules={[{ required: true, message: 'Bạn phải nhập Tên!' }]}
                        >
                            <Input placeholder="Nhập tên" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                        <Form.Item<UnitTypeModel>
                            label="Bảng giá áp dụng"
                            name="feeLadderId"
                            rules={[{ required: true, message: 'Bạn phải chọn Bảng giá áp dụng!' }]}
                        >
                            <Select showSearch placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                {feeLadders.map(ladder => <Select.Option key={ladder.id} value={ladder.id}>{ladder.name}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item<UnitTypeModel>
                            label="Tỉnh/ TP trực thuộc"
                            name="provinceId"
                            rules={[{ required: true, message: 'Bạn phải chọn Tỉnh/ TP!' }]}
                        >
                            <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} disabled={isEdit}>
                                {provinces.map(province => <Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>)}
                            </Select>
                        </Form.Item>
                        <Form.Item<UnitTypeModel>
                            label="Số đặt cọc"
                            name="requiredDepositAmount"
                            rules={[{ required: true, message: 'Bạn phải số đặt cọc!' }]}
                        >
                            <Input type="number" placeholder="Nhập số đặt cọc" style={{width: INPUT_WIDTH}}></Input>
                        </Form.Item>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}